import React, {useMemo} from 'react';
import {Popover, PopoverContent, PopoverTrigger} from '@/shadcn_components/ui/popover';
import {Button} from '@/shadcn_components/ui/button';
import {Input} from '@/shadcn_components/ui/input';
import {AmountCalculationType, AmountParameters, stringifyAmountParameter} from './estimate-store';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shadcn_components/ui/select';
import {Settings2Icon} from 'lucide-react';

interface AmountParametersDialogProps {
  parameters: AmountParameters;
  onSave: (params: AmountParameters) => void;
  readOnly?: boolean;
}

export const AmountParametersDialog: React.FC<AmountParametersDialogProps> = ({
  parameters,
  onSave,
  readOnly = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentParams, setCurrentParams] = React.useState(parameters);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setCurrentParams({...parameters});
    }
    setIsOpen(open);
  };

  const handleSave = () => {
    onSave(currentParams);
    setIsOpen(false);
  };

  const updateParameter = (name: string, value: string | number | AmountCalculationType) => {
    setCurrentParams(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal>
      <div className="flex flex-row justify-end items-center gap-2 w-full">
        <span className="text-sm text-gray-600 truncate">
          {stringifyAmountParameter(currentParams)}
        </span>
        <PopoverTrigger disabled={readOnly} asChild>
          <Button
            variant="ghost"
            size="icon"
            title="Thiết lập tham số tính toán"
            className="h-8 w-8">
            <Settings2Icon size={16} />
          </Button>
        </PopoverTrigger>
      </div>

      <PopoverContent className="w-80" onClick={e => e.stopPropagation()}>
        <div className="font-medium mb-4">Thiết lập tham số tính toán</div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label>Phương thức tính:</label>
            <Select
              value={currentParams.amount_type}
              onValueChange={value =>
                updateParameter('amount_type', value as AmountCalculationType)
              }>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="simple">Chỉ số tiền</SelectItem>
                <SelectItem value="quantity">Số lượng (Số lượng × Đơn giá)</SelectItem>
                <SelectItem value="area">Diện tích (Dài × Rộng × Đơn giá)</SelectItem>
                <SelectItem value="volume">Thể tích (Dài × Rộng × Cao × Đơn giá)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <RenderFields currentParams={currentParams} updateParameter={updateParameter} />

          {/* <div className="space-y-2">
            <label>Chi phí khác (%):</label>
            <Input
              value={currentParams.overhead || 0}
              type="number"
              onChange={e => updateParameter('overhead', Number(e.target.value))}
              placeholder="% chi phí khác"
            />
          </div> */}

          <div className="flex justify-end gap-2 pt-4">
            <Button onClick={() => setIsOpen(false)} variant="outline" size="sm">
              Hủy
            </Button>
            <Button onClick={handleSave} type="submit" size="sm">
              Lưu
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface FieldsProps {
  currentParams: AmountParameters;
  updateParameter: (name: string, value: string | number) => void;
}

const SimpleFields: React.FC<FieldsProps> = ({currentParams, updateParameter}) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <label>Giá</label>
        <Input
          value={currentParams.unit_price || 0}
          type="number"
          onChange={e => updateParameter('unit_price', Number(e.target.value))}
        />
      </div>
    </div>
  );
};

const QuantityFields: React.FC<FieldsProps> = ({currentParams, updateParameter}) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <label>Số lượng:</label>
        <Input
          value={currentParams.quantity || 0}
          type="number"
          onChange={e => updateParameter('quantity', Number(e.target.value))}
        />
      </div>
      <div className="space-y-2">
        <label>Đơn vị tính:</label>
        <Input
          value={currentParams.unit || ''}
          onChange={e => updateParameter('unit', e.target.value)}
          placeholder="m, m2, kg..."
        />
      </div>
      <div className="space-y-2">
        <label>Đơn giá:</label>
        <Input
          value={currentParams.unit_price || 0}
          type="number"
          onChange={e => updateParameter('unit_price', Number(e.target.value))}
        />
      </div>
    </div>
  );
};

const AreaFields: React.FC<FieldsProps> = ({currentParams, updateParameter}) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <label>Chiều rộng:</label>
        <Input
          value={currentParams.width || 0}
          type="number"
          onChange={e => updateParameter('width', Number(e.target.value))}
        />
      </div>
      <div className="space-y-2">
        <label>Chiều dài:</label>
        <Input
          value={currentParams.length || 0}
          type="number"
          onChange={e => updateParameter('length', Number(e.target.value))}
        />
      </div>
      <div className="space-y-2">
        <label>Đơn giá:</label>
        <Input
          value={currentParams.unit_price || 0}
          type="number"
          onChange={e => updateParameter('unit_price', Number(e.target.value))}
        />
      </div>
    </div>
  );
};

const VolumeFields: React.FC<FieldsProps> = ({currentParams, updateParameter}) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <label>Chiều rộng:</label>
        <Input
          value={currentParams.width || 0}
          type="number"
          onChange={e => updateParameter('width', Number(e.target.value))}
        />
      </div>
      <div className="space-y-2">
        <label>Chiều dài:</label>
        <Input
          value={currentParams.length || 0}
          type="number"
          onChange={e => updateParameter('length', Number(e.target.value))}
        />
      </div>
      <div className="space-y-2">
        <label>Chiều cao:</label>
        <Input
          value={currentParams.height || 0}
          type="number"
          onChange={e => updateParameter('height', Number(e.target.value))}
        />
      </div>
      <div className="space-y-2">
        <label>Đơn giá:</label>
        <Input
          value={currentParams.unit_price || 0}
          type="number"
          onChange={e => updateParameter('unit_price', Number(e.target.value))}
        />
      </div>
    </div>
  );
};

const RenderFields: React.FC<FieldsProps> = ({currentParams, updateParameter}) => {
  switch (currentParams.amount_type) {
    case 'volume':
      return <VolumeFields currentParams={currentParams} updateParameter={updateParameter} />;
    case 'area':
      return <AreaFields currentParams={currentParams} updateParameter={updateParameter} />;
    case 'simple':
      return <SimpleFields currentParams={currentParams} updateParameter={updateParameter} />;
    case 'quantity':
    default:
      return <QuantityFields currentParams={currentParams} updateParameter={updateParameter} />;
  }
};
